import * as THREE from "three";
import gsap from "gsap";
// @ts-ignore - Importing from JavaScript files
import { OrbitControls } from "./controls/OrbitControls";
// @ts-ignore - Importing from JavaScript files
import { TransformControls } from "./controls/TransformControls";
import { SceneManager } from "./SceneManager";
import { useSelectionStore, useUIStore } from "@/stores";
import { getCurrentThemeProperties } from "@/utils/themeUtils";
import { GIZMO_EXCEPTION } from "@/constants/exceptions";
import { TARGET } from "@/constants/defaults";
import { Ao } from "../ao/Ao";
import { MODEL_HIGHLIGHT_MAT } from "../../constants/materials";

interface EventHandlers {
  pointerMove?: (e: PointerEvent) => void;
  pointerDown?: (e: PointerEvent) => void;
}

export class ControllerManager {
  private static instance: ControllerManager;

  // Core Three.js objects
  transformControls: TransformControls | null = null;
  private camera: THREE.Camera | null = null;
  private renderer: THREE.WebGLRenderer | null = null;
  private orbitControls: OrbitControls | null = null;

  // State management
  private zoom = 1;
  private isDragging = false;
  private isInitialized = false;
  private isGizmoHovered = false;
  private isGizmoInteracting = false;

  isLightSettingMode: boolean = false;

  // Hover and selection state
  private hoveredObject: THREE.Object3D | null = null;
  selectedObject: THREE.Object3D | null = null;

  // Raycasting
  private raycaster = new THREE.Raycaster();
  private pointerNdc = new THREE.Vector2();
  private lastPointerNdc = new THREE.Vector2(999, 999);

  // Materials
  private highlightMaterial!: THREE.MeshStandardMaterial;
  private selectedMaterial!: THREE.MeshStandardMaterial;

  // Event handling
  private eventHandlers: EventHandlers = {};
  private rafId: number | null = null;
  private isHoverEnabled = false;
  private isSelectionEnabled = false;
  private renderNeedsUpdate = true;

  // Gizmo highlighting
  private gizmoRafId: number | null = null;
  private themeColors: {
    primary: string;
    secondary: string;
    accent: string;
  } | null = null;

  // Attachment baselines captured at first selection per parent key
  private attachmentBaselineByKey: Record<string, Record<string, number>> = {};

  // Local history for attachment baselines (kept in sync with selectionStore history
  // when gizmo-driven transforms occur)
  private attachmentHistoryUndoStack: {
    baselines: Record<string, Record<string, number>>;
    selectedParentKey?: string | null;
  }[] = [];

  private attachmentHistoryRedoStack: {
    baselines: Record<string, Record<string, number>>;
    selectedParentKey?: string | null;
  }[] = [];

  // Gizmo exception configuration
  private gizmoException: any = GIZMO_EXCEPTION;

  private constructor() {
    this.initializeMaterials();
  }

  static getInstance(): ControllerManager {
    if (!ControllerManager.instance) {
      ControllerManager.instance = new ControllerManager();
    }
    return ControllerManager.instance;
  }

  private initializeMaterials(): void {
    this.highlightMaterial = new THREE.MeshStandardMaterial(
      MODEL_HIGHLIGHT_MAT
    );

    this.selectedMaterial = new THREE.MeshStandardMaterial(MODEL_HIGHLIGHT_MAT);

    // Load HDR environment map for the materials
    //this.loadHDRForMaterials();
  }

  private async loadHDRForMaterials(): Promise<void> {
    try {
      const { LightsManager } =
        await import("@/services/lighting/LightsManager");
      const lightsManager = LightsManager.getInstance();

      if (lightsManager.getInitialized()) {
        await lightsManager.loadHDRForMaterial(this.highlightMaterial, 1.0);
        await lightsManager.loadHDRForMaterial(this.selectedMaterial, 1.0);
      }
    } catch (error) {
      console.warn("Failed to load HDR for materials:", error);
    }
  }

  async initialize(
    camera: THREE.Camera,
    renderer: THREE.WebGLRenderer
  ): Promise<void> {
    if (this.isInitialized) {
      console.warn("ControllerManager already initialized");
      return;
    }

    try {
      this.camera = camera;
      this.renderer = renderer;
      await this.setupControls();
      this.setupEventHandlers();
      this.isInitialized = true;
    } catch (error) {
      console.error("Failed to initialize controller manager:", error);
      throw error;
    }
  }

  private async setupControls(): Promise<void> {
    if (!this.camera || !this.renderer) return;

    // Setup transform controls
    this.transformControls = new TransformControls(
      this.camera,
      this.renderer.domElement
    );
    this.transformControls.space = "local";
    this.transformControls.setSize(0.5);

    // Add to scene
    const scene = SceneManager.getInstance().scene;
    if (scene && !scene.children.includes(this.transformControls)) {
      scene.add(this.transformControls as TransformControls);
    }

    // Setup orbit controls
    this.orbitControls = new OrbitControls(
      this.camera,
      this.renderer.domElement
    );
    this.orbitControls.minPolarAngle = 0;
    this.orbitControls.maxPolarAngle = Math.PI / 1.9;
    this.orbitControls.minDistance = 50;
    this.orbitControls.maxDistance = 355;
    this.orbitControls.enablePan = true;
    this.orbitControls.zoomToCursor = true;
    this.orbitControls.addEventListener("change", () => {
      this.renderNeedsUpdate = true;
    });
  }

  private setupEventHandlers(): void {
    if (!this.transformControls) return;
    const ao = Ao.getInstance();
    // Transform controls dragging handler
    this.transformControls.addEventListener("dragging-changed", (e: any) => {
      this.isDragging = !!e.value;
      this.handleDraggingChange();
    });

    this.transformControls.addEventListener("mouseDown", (e: any) => {
      const store = useSelectionStore.getState();
      // Capture current attachment baselines in local history before
      // any gizmo-driven transform changes, so undo/redo can restore them.
      this.captureAttachmentHistoryFrame();
      store.saveToHistory();
    });

    // Listen for gizmo axis changes to detect hover/click on gizmo
    this.transformControls.addEventListener("axis-changed", (e: any) => {
      if (!this.isLightSettingMode) this.handleGizmoAxisChange(e.value);
    });

    // Listen for object changes during dragging to ensure we capture the final state
    this.transformControls.addEventListener("objectChange", () => {
      if (this.isDragging && this.selectedObject) {
        this.persistSelectedTransform();
        if (ao?.aoEnabled) ao.sceneUpdated();
      }
    });
  }

  private handleDraggingChange(): void {
    if (this.orbitControls) {
      this.orbitControls.enabled = !this.isDragging;
    }

    if (this.isDragging) {
      this.isGizmoInteracting = true;
    } else {
      this.isGizmoInteracting = false;
      // Save transform when dragging ends to capture final values
      this.persistSelectedTransform();
    }
  }

  private handleGizmoAxisChange(axis: string | null): void {
    const wasGizmoHovered = this.isGizmoHovered;
    this.isGizmoHovered = axis !== null;

    // If we started hovering over gizmo, disable hover picking and selection
    if (!wasGizmoHovered && this.isGizmoHovered) {
      this.disableHoverPicking();
      this.disableSelection();
    }
    // If we stopped hovering over gizmo, re-enable hover picking and selection
    else if (wasGizmoHovered && !this.isGizmoHovered) {
      this.enableHoverPicking();
      this.enableSelection();
    }
  }

  enableHoverPicking(): void {
    if (!this.renderer || !this.camera || this.isHoverEnabled) return;

    this.eventHandlers.pointerMove = (event: PointerEvent) =>
      this.handlePointerMove(event);
    this.renderer.domElement.addEventListener(
      "pointermove",
      this.eventHandlers.pointerMove,
      {
        passive: true
      }
    );

    this.isHoverEnabled = true;
  }

  disableHoverPicking(): void {
    if (!this.renderer || !this.isHoverEnabled) return;

    if (this.eventHandlers.pointerMove) {
      this.renderer.domElement.removeEventListener(
        "pointermove",
        this.eventHandlers.pointerMove
      );
      this.eventHandlers.pointerMove = undefined;
    }

    this.cancelRaf();
    this.clearHover();
    this.isHoverEnabled = false;
  }

  enableSelection(): void {
    if (!this.renderer || this.isSelectionEnabled) return;

    this.eventHandlers.pointerDown = (event: PointerEvent) =>
      this.handlePointerDown(event);
    this.renderer.domElement.addEventListener(
      "pointerdown",
      this.eventHandlers.pointerDown
    );

    this.isSelectionEnabled = true;
  }

  disableSelection(): void {
    if (!this.renderer || !this.isSelectionEnabled) return;

    if (this.eventHandlers.pointerDown) {
      this.renderer.domElement.removeEventListener(
        "pointerdown",
        this.eventHandlers.pointerDown
      );
      this.eventHandlers.pointerDown = undefined;
    }

    this.isSelectionEnabled = false;
  }

  enableTransformControls(): void {
    if (!this.transformControls) return;
    (this.transformControls as any).enabled = true;
  }

  setTransformMode(mode: "translate" | "rotate" | "scale"): void {
    if (!this.transformControls) return;

    const transformControls = this.transformControls as any;

    // Check if the requested mode is allowed
    if (
      transformControls.userData.allowedModes &&
      !transformControls.userData.allowedModes.includes(mode)
    ) {
      console.warn(`Transform mode '${mode}' is not allowed for this asset`);
      return;
    }

    transformControls.mode = mode;

    // Reapply movement restrictions when switching modes
    if (this.selectedObject) {
      this.applyMovementRestrictions(this.selectedObject);
    }
  }

  resetAttachmentBaseline(): void {
    this.attachmentBaselineByKey = {};
    this.attachmentHistoryUndoStack = [];
    this.attachmentHistoryRedoStack = [];
  }

  disableTransformControls(): void {
    if (!this.transformControls) return;

    this.transformControls.visible = false;
    (this.transformControls as any).enabled = false;

    this.clearSelection();
  }

  clearSelection(): void {
    // Restore material on previously selected hierarchy, if any
    if (this.selectedObject) {
      this.restoreObjectMaterial(this.selectedObject, false);
    }

    // Always detach and hide gizmo, even if selectedObject is already null.
    if (this.transformControls) {
      this.transformControls.detach();
      this.transformControls.visible = false;
      // Reset movement restrictions when clearing selection
      this.resetMovementRestrictions();
    }

    this.selectedObject = null;
    // Only clear pending selection restore if not during undo/redo
    // (it will be cleared after restoration completes)
  }

  /**
   * Capture current attachment baseline state into local history
   * (kept in lock-step with selectionStore.saveToHistory when invoked
   * from gizmo interactions).
   */
  private captureAttachmentHistoryFrame(): void {
    // Clone baselines to avoid mutation issues
    const clonedBaselines: Record<string, Record<string, number>> = {};
    for (const key of Object.keys(this.attachmentBaselineByKey)) {
      clonedBaselines[key] = { ...this.attachmentBaselineByKey[key] };
    }

    // Store the currently selected object's parent key in the history frame
    const selectedParentKey = this.selectedObject?.userData?.parent || null;

    this.attachmentHistoryUndoStack.push({
      baselines: clonedBaselines,
      selectedParentKey
    });

    // Trim undo stack to a reasonable size (match selectionStore HISTORY_LIMIT)
    const HISTORY_LIMIT = 10;
    if (this.attachmentHistoryUndoStack.length > HISTORY_LIMIT) {
      this.attachmentHistoryUndoStack.shift();
    }

    // Any new action invalidates redo history
    this.attachmentHistoryRedoStack = [];
  }

  /**
   * Apply an undo step for attachment baselines, if we have history.
   * This is intended to be called in tandem with selectionStore.undo()
   * once the scene has been updated.
   */
  applyAttachmentUndoFromHistory(): {
    currentSelection: string | null;
    historySelection: string | null;
  } {
    if (this.attachmentHistoryUndoStack.length === 0) {
      return { currentSelection: null, historySelection: null };
    }

    const current: Record<string, Record<string, number>> = {};
    for (const key of Object.keys(this.attachmentBaselineByKey)) {
      current[key] = { ...this.attachmentBaselineByKey[key] };
    }

    const previousFrame =
      this.attachmentHistoryUndoStack[
        this.attachmentHistoryUndoStack.length - 1
      ];
    this.attachmentHistoryUndoStack = this.attachmentHistoryUndoStack.slice(
      0,
      -1
    );

    // Store current selection parent key for redo and to potentially maintain it
    const currentSelectedParentKey =
      this.selectedObject?.userData?.parent || null;

    // Push current into redo stack
    this.attachmentHistoryRedoStack.unshift({
      baselines: current,
      selectedParentKey: currentSelectedParentKey
    });

    // Restore previous baselines
    this.attachmentBaselineByKey = {};
    for (const key of Object.keys(previousFrame.baselines)) {
      this.attachmentBaselineByKey[key] = { ...previousFrame.baselines[key] };
    }

    // Clear selection temporarily to restore baselines
    // Selection will be restored after scene is rebuilt
    this.clearSelection();

    // Return both current selection and history selection to allow prioritizing current
    return {
      currentSelection: currentSelectedParentKey,
      historySelection: previousFrame.selectedParentKey || null
    };
  }

  /**
   * Apply a redo step for attachment baselines, if we have history.
   * This is intended to be called in tandem with selectionStore.redo()
   * once the scene has been updated.
   */
  applyAttachmentRedoFromHistory(): {
    currentSelection: string | null;
    historySelection: string | null;
  } {
    if (this.attachmentHistoryRedoStack.length === 0) {
      return { currentSelection: null, historySelection: null };
    }

    const current: Record<string, Record<string, number>> = {};
    for (const key of Object.keys(this.attachmentBaselineByKey)) {
      current[key] = { ...this.attachmentBaselineByKey[key] };
    }

    const nextFrame = this.attachmentHistoryRedoStack[0];
    this.attachmentHistoryRedoStack = this.attachmentHistoryRedoStack.slice(1);

    // Store current selection parent key for undo and to potentially maintain it
    const currentSelectedParentKey =
      this.selectedObject?.userData?.parent || null;

    // Push current into undo stack
    this.attachmentHistoryUndoStack.push({
      baselines: current,
      selectedParentKey: currentSelectedParentKey
    });

    // Restore next baselines
    this.attachmentBaselineByKey = {};
    for (const key of Object.keys(nextFrame.baselines)) {
      this.attachmentBaselineByKey[key] = { ...nextFrame.baselines[key] };
    }

    // Clear selection temporarily to restore baselines
    // Selection will be restored after scene is rebuilt
    this.clearSelection();

    // Return both current selection and history selection to allow prioritizing current
    return {
      currentSelection: currentSelectedParentKey,
      historySelection: nextFrame.selectedParentKey || null
    };
  }

  /**
   * Returns true if transform controls are present and enabled.
   * Used by undo/redo callers to decide whether to sync attachment history.
   */
  isGizmoActive(): boolean {
    if (!this.transformControls) return false;
    return !!(this.transformControls as any).enabled;
  }

  /**
   * Find an object in the scene by its parent key (userData.parent)
   */
  private findObjectByParentKey(parentKey: string): THREE.Object3D | null {
    const scene = SceneManager.getInstance().scene;
    if (!scene) return null;

    let found: THREE.Object3D | null = null;

    scene.traverse((obj: THREE.Object3D) => {
      const objParent = obj.userData?.parent;
      if (objParent === parentKey) {
        found = obj;
      }
    });

    return found;
  }

  /**
   * Restore selection by finding the object with the given parent key
   * and re-attaching the gizmo to it.
   */
  private restoreSelectionByParentKey(parentKey: string): void {
    if (!this.transformControls || !this.isGizmoActive()) return;

    const object = this.findObjectByParentKey(parentKey);
    if (object) {
      // Re-select the object
      this.selectedObject = object;
      this.updateObjectMaterial(object, this.selectedMaterial);

      // Re-attach gizmo
      if (this.transformControls) {
        this.transformControls.attach(object);
        this.transformControls.visible = true;

        // Reapply movement restrictions
        this.applyMovementRestrictions(object);
      }
    }
  }

  /**
   * Restore selection after scene has been rebuilt.
   * Prioritizes maintaining current selection if it still exists.
   * This should be called after undo/redo operations complete.
   */
  restoreSelection(
    currentSelection: string | null,
    historySelection: string | null
  ): void {
    // Prioritize current selection - if it still exists, maintain it
    // Otherwise, restore from history
    const parentKeyToRestore = currentSelection || historySelection;

    if (!parentKeyToRestore) {
      // If there's no selection to restore, ensure gizmo is hidden
      if (this.transformControls) {
        this.transformControls.visible = false;
      }
      return;
    }

    // Use requestAnimationFrame to ensure scene has been fully updated
    requestAnimationFrame(() => {
      // First try to restore current selection if it was provided
      if (currentSelection) {
        const object = this.findObjectByParentKey(currentSelection);
        if (object) {
          // Current selection still exists, maintain it
          this.restoreSelectionByParentKey(currentSelection);
          return;
        }
      }

      // Current selection doesn't exist or wasn't provided, try history selection
      if (historySelection) {
        const object = this.findObjectByParentKey(historySelection);
        if (object) {
          this.restoreSelectionByParentKey(historySelection);
          return;
        }
      }

      // Neither selection exists, hide gizmo
      if (this.transformControls) {
        this.transformControls.visible = false;
      }
    });
  }

  /**
   * Check if the currently selected object still exists in the scene.
   * If it exists, maintain selection; if not, clear selection and hide gizmo.
   * This should be called after assets are changed via the menu.
   */
  checkAndMaintainSelection(): void {
    if (!this.isGizmoActive() || !this.selectedObject) {
      return;
    }

    const parentKey = this.selectedObject.userData?.parent;
    if (!parentKey) {
      // No parent key, clear selection
      this.clearSelection();
      return;
    }

    const object = this.findObjectByParentKey(parentKey);
    if (object) {
      // Object still exists, maintain selection by updating reference and re-attaching gizmo
      this.selectedObject = object;
      // Update material to maintain visual selection
      this.updateObjectMaterial(object, this.selectedMaterial);

      if (this.transformControls) {
        this.transformControls.attach(object);
        this.transformControls.visible = true;
        // Reapply movement restrictions
        this.applyMovementRestrictions(object);
      }
    } else {
      // Object no longer exists, clear selection and hide gizmo
      this.clearSelection();
    }
  }

  private handlePointerMove(event: PointerEvent): void {
    if (
      !this.renderer ||
      !this.camera ||
      !this.isHoverEnabled ||
      this.isDragging ||
      this.isGizmoHovered ||
      this.isGizmoInteracting
    )
      return;

    this.updatePointerPosition(event);

    if (this.shouldSkipPointerMove()) return;

    this.scheduleHoverRaycast();
  }

  private updatePointerPosition(event: PointerEvent): void {
    const rect = this.renderer!.domElement.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    const y = -(((event.clientY - rect.top) / rect.height) * 2 - 1);
    this.pointerNdc.set(x, y);
  }

  private shouldSkipPointerMove(): boolean {
    const deltaX = Math.abs(this.pointerNdc.x - this.lastPointerNdc.x);
    const deltaY = Math.abs(this.pointerNdc.y - this.lastPointerNdc.y);

    if (deltaX < 0.001 && deltaY < 0.001) return true;

    this.lastPointerNdc.copy(this.pointerNdc);
    return false;
  }

  private scheduleHoverRaycast(): void {
    if (this.rafId !== null) return; // coalesce events

    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.performHoverRaycast();
    });
  }

  private cancelRaf(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private getSelectableRoot(object: THREE.Object3D): THREE.Object3D | null {
    let current: THREE.Object3D | null = object;
    while (current) {
      if (current.userData?.parent) return current;
      current = current.parent;
    }

    return object.parent || object;
  }

  private performHoverRaycast(): void {
    if (!this.camera) return;

    const scene = SceneManager.getInstance().scene;
    if (!scene?.characterContainer) return;

    this.raycaster.setFromCamera(this.pointerNdc, this.camera as THREE.Camera);

    // Include both characterContainer and baseContainer children for raycasting
    const raycastTargets = [...scene.characterContainer.children];
    if (scene.baseContainer) {
      raycastTargets.push(...scene.baseContainer.children);
    }

    const intersections = this.raycaster.intersectObjects(raycastTargets, true);
    if (intersections.length > 0) {
      const object = intersections[0].object as THREE.Object3D;
      //const hoverTarget = this.getSelectableRoot(object);
      this.applyHover(object.parent);
    } else this.clearHover();
  }

  private applyHover(object: THREE.Object3D | null): void {
    this.clearHover();

    if (object) {
      this.hoveredObject = object;
      // Only apply highlight material if the object is not currently selected
      if (object !== this.selectedObject) {
        this.updateObjectMaterial(object, this.highlightMaterial);
      }
    }
  }

  private clearHover(): void {
    if (this.hoveredObject && this.hoveredObject !== this.selectedObject) {
      this.restoreObjectMaterial(this.hoveredObject);
    }
    this.hoveredObject = null;
  }

  private handlePointerDown(event: PointerEvent): void {
    if (
      !this.hoveredObject ||
      !this.transformControls ||
      !this.isSelectionEnabled ||
      this.isGizmoHovered ||
      this.isGizmoInteracting
    )
      return;

    this.setSelectedObject(this.hoveredObject);
  }

  private setSelectedObject(object: THREE.Object3D): void {
    this.clearSelection();

    this.selectedObject = object?.parent || object;
    this.updateObjectMaterial(object?.parent || object, this.selectedMaterial);

    if (this.transformControls) {
      this.transformControls.attach(object?.parent || object);
      this.transformControls.visible = true;

      // Apply movement restrictions based on asset name
      this.applyMovementRestrictions(object?.parent || object);
    }

    // Capture baseline attachment data on first selection
    try {
      const parentKey = this.selectedObject?.userData?.parent;
      if (!parentKey) return;

      if (!this.attachmentBaselineByKey[parentKey]) {
        const store = useSelectionStore.getState();
        const existing = store.getSelectedAsset(parentKey);

        const baseline = existing?.assetNode?.attachmentData;
        if (baseline) {
          this.attachmentBaselineByKey[parentKey] = { ...baseline };
        } else {
          console.warn(`No baseline data found for ${parentKey}`);
        }
      }
    } catch (error) {
      console.warn("Failed to capture attachment baseline:", error);
    }
  }

  private updateObjectMaterial(
    object: THREE.Object3D,
    material: THREE.Material
  ): void {
    object.traverse((child) => {
      if ((child as THREE.Mesh).isMesh && (child as THREE.Mesh).material) {
        const mesh = child as THREE.Mesh;
        // Store original material on the mesh itself
        if (!(mesh as any).userData.originalMaterial) {
          (mesh as any).userData.originalMaterial = mesh.material;
        }
        mesh.material = material;
      }
    });
  }

  private isChildOfSelectedObject(child: THREE.Object3D): boolean {
    if (!this.selectedObject) return false;
    // Check if the child is the selected object itself
    if (child === this.selectedObject) return true;
    // Check if the child is a descendant of the selected object
    let parent = child.parent;
    while (parent) {
      if (parent === this.selectedObject) return true;
      parent = parent.parent;
    }

    return false;
  }

  private getAssetExceptionConfig(object: THREE.Object3D): any | null {
    if (!this.gizmoException) return null;

    const assetName = object.name || object.userData?.name;
    if (!assetName)
      return object.parent ? this.getAssetExceptionConfig(object.parent) : null;

    return (
      this.gizmoException[assetName] ||
      (object.parent ? this.getAssetExceptionConfig(object.parent) : null)
    );
  }

  private applyMovementRestrictions(object: THREE.Object3D): void {
    if (!this.gizmoException || !this.transformControls) return;

    const transformControls = this.transformControls as any;
    this.storeOriginalSettings(transformControls);

    const assetConfig = this.getAssetExceptionConfig(object);
    if (
      !assetConfig ||
      !assetConfig?.movement ||
      (!assetConfig.appliesToChildren &&
        object.parent?.name !== assetConfig.name)
    ) {
      this.applyDefaultSettings(transformControls);
      return;
    }

    this.applyAssetRestrictions(assetConfig, transformControls);
    this.applyRestrictionsToChildren(object, assetConfig);
    this.updateGizmoVisibility();
  }

  private storeOriginalSettings(transformControls: any): void {
    if (!transformControls.userData.originalTransformSettings) {
      transformControls.userData.originalTransformSettings = {
        showX: transformControls.showX,
        showY: transformControls.showY,
        showZ: transformControls.showZ,
        mode: transformControls.mode
      };
    }
  }

  private applyDefaultSettings(transformControls: any): void {
    transformControls.userData.allowedModes = ["translate", "rotate", "scale"];
    transformControls.showX = true;
    transformControls.showY = true;
    transformControls.showZ = true;
    this.updateGizmoVisibility();
  }

  private applyAssetRestrictions(
    assetConfig: any,
    transformControls: any
  ): void {
    const { movement } = assetConfig;
    this.applyMovementTypeRestrictions(movement, transformControls);
    this.applyAxisRestrictions(movement, transformControls);
  }

  private applyMovementTypeRestrictions(
    movement: any,
    transformControls: any
  ): void {
    const allowedModes = [];

    if (movement.rotation) allowedModes.push("rotate");
    if (movement.translation) allowedModes.push("translate");
    if (movement.scale) allowedModes.push("scale");

    transformControls.userData.allowedModes =
      allowedModes.length > 0 ? allowedModes : ["translate", "rotate", "scale"];
  }

  private applyAxisRestrictions(movement: any, transformControls: any): void {
    const axisConfig = this.getAxisConfiguration(movement);
    transformControls.showX = axisConfig.x;
    transformControls.showY = axisConfig.y;
    transformControls.showZ = axisConfig.z;
  }

  private getAxisConfiguration(movement: any): {
    x: boolean;
    y: boolean;
    z: boolean;
  } {
    // Priority: rotation > translation > scale > default (all enabled)
    const movementTypes = ["rotation", "translation", "scale"] as const;

    for (const type of movementTypes) {
      if (movement[type] && Array.isArray(movement[type])) {
        const allowedAxes = movement[type].map((axis: string) =>
          axis.toLowerCase()
        );
        return {
          x: allowedAxes.includes("x"),
          y: allowedAxes.includes("y"),
          z: allowedAxes.includes("z")
        };
      }
    }

    return { x: true, y: true, z: true };
  }

  private updateGizmoVisibility(): void {
    if (!this.transformControls) return;

    const transformControls = this.transformControls as any;
    const axisVisibility = {
      x: transformControls.showX,
      y: transformControls.showY,
      z: transformControls.showZ
    };

    // Update both gizmo and picker visibility
    const gizmoTypes = ["gizmo", "picker"] as const;
    gizmoTypes.forEach((gizmoType) => {
      const gizmo = transformControls[gizmoType]?.rotate;
      if (gizmo) {
        this.updateGizmoChildrenVisibility(gizmo, axisVisibility);
      }
    });
  }

  private updateGizmoChildrenVisibility(
    gizmo: any,
    axisVisibility: { x: boolean; y: boolean; z: boolean }
  ): void {
    gizmo.children.forEach((child: THREE.Object3D) => {
      const name = child.name.toLowerCase();
      if (name.includes("x")) child.visible = axisVisibility.x;
      else if (name.includes("y")) child.visible = axisVisibility.y;
      else if (name.includes("z")) child.visible = axisVisibility.z;
    });
  }

  private resetMovementRestrictions(): void {
    if (!this.transformControls) return;

    const transformControls = this.transformControls as any;
    const originalSettings =
      transformControls.userData.originalTransformSettings;

    if (originalSettings) {
      transformControls.showX = originalSettings.showX;
      transformControls.showY = originalSettings.showY;
      transformControls.showZ = originalSettings.showZ;
      delete transformControls.userData.allowedModes;
      this.updateGizmoVisibility();
    }
  }

  private applyRestrictionsToChildren(
    object: THREE.Object3D,
    assetConfig: any
  ): void {
    if (!assetConfig.appliesToChildren) return;

    object.traverse((child) => {
      if (child !== object) {
        child.userData.gizmoException = assetConfig;
      }
    });
  }

  private restoreObjectMaterial(
    object: THREE.Object3D,
    skipSelectedChildren: boolean = true
  ): void {
    object.traverse((child) => {
      // Skip if this child is the selected object or part of the selected object's hierarchy
      if (skipSelectedChildren && this.isChildOfSelectedObject(child)) return;

      if (
        (child as THREE.Mesh).isMesh &&
        (child as any).userData.originalMaterial
      ) {
        const mesh = child as THREE.Mesh;
        mesh.material = (mesh as any).userData.originalMaterial;
      }
    });
  }

  private persistSelectedTransform(): void {
    if (!this.selectedObject) {
      return;
    }

    try {
      const parentKey = this.selectedObject.userData?.parent;
      if (!parentKey) {
        return;
      }

      const store = useSelectionStore.getState();
      const uiStore = useUIStore.getState();
      const existing = store.getSelectedAsset(parentKey);

      if (!existing) {
        return;
      }
      uiStore.setPoseChanged(true);
      const pos = this.selectedObject.position;
      const rot = this.selectedObject.rotation;
      const scl = this.selectedObject.scale;
      const toDeg = (r: number) => (r * 180) / Math.PI;

      const attachmentData: Record<string, number> = {
        tx: pos.x,
        ty: pos.y,
        tz: pos.z,
        rx: toDeg(rot.x),
        ry: toDeg(rot.y),
        rz: toDeg(rot.z),
        sx: scl.x,
        sy: scl.y,
        sz: scl.z
      };

      existing.assetNode.attachmentData = {
        ...attachmentData
      };

      store.setSelectedAsset(parentKey, {
        ...existing
      });
    } catch (error) {
      console.error("Failed to persist transform:", error);
    }
  }

  hasSelection(): boolean {
    return !!this.selectedObject;
  }

  setGizmoException(exception: any): void {
    this.gizmoException = exception;
  }

  getGizmoException(): any {
    return this.gizmoException;
  }

  getAllowedModes(): string[] {
    if (!this.transformControls) return ["translate", "rotate", "scale"];

    const transformControls = this.transformControls as any;
    return (
      transformControls.userData?.allowedModes ?? [
        "translate",
        "rotate",
        "scale"
      ]
    );
  }

  getSelectedObjectParent(): string | null {
    return this.selectedObject?.userData?.parent || null;
  }

  getSelectedObjectName(): string | null {
    return (
      this.selectedObject?.name || this.selectedObject?.userData?.name || null
    );
  }

  resetSelectedAttachment(): void {
    if (!this.selectedObject) {
      console.warn("No selected object to reset");
      return;
    }

    try {
      const parentKey = this.selectedObject.userData?.parent;
      if (!parentKey) {
        console.warn("No parent key found for selected object");
        return;
      }

      const baseline = this.attachmentBaselineByKey[parentKey];
      if (!baseline) {
        console.warn(`No baseline found for ${parentKey}`);
        return;
      }

      console.log(
        `Resetting selected object ${
          this.selectedObject.name || "unnamed"
        } to baseline:`,
        baseline
      );

      // Apply transform to object from baseline (degrees -> radians for rotation)
      const toRad = (d: number) => (d * Math.PI) / 180;
      this.selectedObject.position.set(
        baseline.tx ?? this.selectedObject.position.x,
        baseline.ty ?? this.selectedObject.position.y,
        baseline.tz ?? this.selectedObject.position.z
      );
      this.selectedObject.rotation.set(
        toRad(baseline.rx ?? 0),
        toRad(baseline.ry ?? 0),
        toRad(baseline.rz ?? 0)
      );
      this.selectedObject.scale.set(
        baseline.sx ?? this.selectedObject.scale.x,
        baseline.sy ?? this.selectedObject.scale.y,
        baseline.sz ?? this.selectedObject.scale.z
      );

      // Update store attachmentData back to baseline
      const store = useSelectionStore.getState();
      store.saveToHistory();
      const existing = store.getSelectedAsset(parentKey);
      if (existing) {
        existing.assetNode.attachmentData = { ...baseline } as Record<
          string,
          number
        >;
        store.setSelectedAsset(parentKey, { ...existing });
      }

      // Ensure controls update
      if (this.transformControls && this.transformControls.object) {
        this.transformControls.updateMatrixWorld();
      }
    } catch (error) {
      console.error("Failed to reset attachment:", error);
    }
  }

  resetCamera(object: THREE.Object3D, transition: boolean = false): void {
    if (!this.camera || !(this.camera instanceof THREE.PerspectiveCamera))
      return;
    if (!this.orbitControls) return;

    // Always reset target to default TARGET position (where character is centered)
    const defaultTarget = new THREE.Vector3(TARGET.x, TARGET.y, TARGET.z);
    const defaultPosition = new THREE.Vector3(10, 30, 90);

    // Calculate the final fitted position from default position/target
    const boundingBox = new THREE.Box3().setFromObject(object);
    const size = boundingBox.getSize(new THREE.Vector3());
    const fov = this.camera.fov * (Math.PI / 180);
    const margin = 1.7;
    const distanceFromTarget = (size.y * margin) / (2 * Math.tan(fov / 2));

    // Calculate direction from default position to default target
    const direction = new THREE.Vector3()
      .subVectors(defaultPosition, defaultTarget)
      .normalize();

    // Final position: fitted distance from default target
    const finalPosition = direction
      .multiplyScalar(distanceFromTarget)
      .add(defaultTarget);

    if (transition) {
      // Single smooth animation to final fitted position and default target
      this.moveCameraSmoothly(finalPosition, defaultTarget, 1.0);
    } else {
      // Instant reset to final fitted position and default target
      this.camera.position.set(
        finalPosition.x,
        finalPosition.y,
        finalPosition.z
      );
      this.orbitControls.target.set(
        defaultTarget.x,
        defaultTarget.y,
        defaultTarget.z
      );
      this.orbitControls.update();
    }
  }

  private moveCameraSmoothly(
    newPos: THREE.Vector3,
    newTarget?: THREE.Vector3,
    duration: number = 0.7,
    onComplete?: () => void
  ) {
    if (!this.camera || !(this.camera instanceof THREE.PerspectiveCamera))
      return;
    if (!this.orbitControls) return;

    let completedAnimations = 0;
    const totalAnimations = newTarget ? 2 : 1;
    const checkComplete = () => {
      completedAnimations++;
      if (completedAnimations === totalAnimations && onComplete) {
        onComplete();
      }
    };

    gsap.to(this.camera.position, {
      x: newPos.x,
      y: newPos.y,
      z: newPos.z,
      duration,
      ease: "power3.inOut",
      onUpdate: () => {
        this.orbitControls?.update();
      },
      onComplete: checkComplete
    });

    if (newTarget) {
      gsap.to(this.orbitControls.target, {
        x: newTarget.x,
        y: newTarget.y,
        z: newTarget.z,
        duration,
        ease: "power3.inOut",
        onUpdate: () => {
          this.orbitControls?.update();
        },
        onComplete: checkComplete
      });
    }
  }

  private applyThemeColorsToGizmos(): void {
    if (!this.transformControls || !this.themeColors) return;

    const { primary, secondary, accent } = this.themeColors;
    const primaryColor = new THREE.Color(primary);
    const secondaryColor = new THREE.Color(secondary);
    const accentColor = new THREE.Color(accent);

    this.transformControls.traverse((child) => {
      if (child instanceof THREE.Mesh && child.material) {
        this.applyGizmoMaterialTheme(
          child,
          primaryColor,
          secondaryColor,
          accentColor
        );
      }
    });

    this.setupGizmoHighlighting();
  }

  private applyGizmoMaterialTheme(
    child: THREE.Mesh,
    primaryColor: THREE.Color,
    secondaryColor: THREE.Color,
    accentColor: THREE.Color
  ): void {
    const material = child.material as THREE.MeshStandardMaterial;

    if (!material.userData.originalColor) {
      material.userData.originalColor = material.color.clone();
    }

    const colorMap: Record<string, THREE.Color> = {
      X: primaryColor,
      Y: secondaryColor,
      Z: accentColor
    };

    const axis = this.getGizmoAxis(child.name);
    const color = colorMap[axis] || secondaryColor;

    material.color.copy(color);
    material.userData.originalColor.copy(color);
  }

  private getGizmoAxis(name: string): string {
    if (name.includes("X") || name.includes("x")) return "X";
    if (name.includes("Y") || name.includes("y")) return "Y";
    if (name.includes("Z") || name.includes("z")) return "Z";
    return "";
  }

  private setupGizmoHighlighting(): void {
    if (!this.transformControls) return;

    // Simple event-based highlighting instead of RAF loop
    this.transformControls.addEventListener("change", () => {
      this.updateGizmoHighlighting();
    });

    this.transformControls.addEventListener("objectChange", () => {
      this.updateGizmoHighlighting();
    });
  }

  private updateGizmoHighlighting(): void {
    if (!this.transformControls || !this.themeColors) return;

    const secondaryColor = new THREE.Color(this.themeColors.secondary);
    const currentAxis = (this.transformControls as any).axis;

    this.transformControls.traverse((child) => {
      if (child instanceof THREE.Mesh && child.material) {
        const material = child.material as THREE.MeshStandardMaterial;

        // Reset to original color
        if (material.userData.originalColor) {
          material.color.copy(material.userData.originalColor);
        }

        // Apply highlight for active axis
        if (currentAxis && this.isAxisActive(child.name, currentAxis)) {
          material.color.copy(secondaryColor);
          material.opacity = 1.0;
        }
      }
    });
  }

  private isAxisActive(childName: string, currentAxis: string): boolean {
    return childName === currentAxis || childName.includes(currentAxis);
  }

  reset(): void {
    try {
      this.cleanup();
      this.resetState();
    } catch (error) {
      console.error("Failed to reset controller manager:", error);
    }
  }

  private cleanup(): void {
    // Stop all RAF loops
    this.cancelRaf();
    this.cancelGizmoRaf();

    // Remove from scene
    const scene = SceneManager.getInstance().scene;
    if (scene && this.transformControls) {
      scene.remove(this.transformControls as unknown as THREE.Object3D);
    }

    // Remove event listeners
    this.removeAllEventListeners();

    // Disable interactions
    this.disableHoverPicking();
    this.disableSelection();

    // Dispose Three.js objects
    this.disposeThreeObjects();
  }

  private removeAllEventListeners(): void {
    this.eventHandlers = {};
  }

  private disposeThreeObjects(): void {
    if (this.orbitControls) {
      this.orbitControls.dispose();
      this.orbitControls = null;
    }

    if (this.transformControls) {
      this.transformControls.dispose();
      this.transformControls = null;
    }

    // Dispose materials
    this.highlightMaterial.dispose();
    this.selectedMaterial.dispose();
  }

  private resetState(): void {
    this.camera = null;
    this.renderer = null;
    this.zoom = 1;
    this.isInitialized = false;
    this.isDragging = false;
    this.isGizmoHovered = false;
    this.isGizmoInteracting = false;
    this.hoveredObject = null;
    this.selectedObject = null;
    this.themeColors = null;
    this.attachmentBaselineByKey = {};
    this.gizmoException = GIZMO_EXCEPTION;
  }

  private cancelGizmoRaf(): void {
    if (this.gizmoRafId) {
      cancelAnimationFrame(this.gizmoRafId);
      this.gizmoRafId = null;
    }
  }

  // Reset attachments for all currently selected assets to their baselines (or initial attachmentData)
  resetAllAttachments(): void {
    try {
      const scene = SceneManager.getInstance().scene;
      if (!scene) return;
      const store = useSelectionStore.getState();
      store.saveToHistory();
      const toRad = (d: number) => (d * Math.PI) / 180;

      for (const key in this.attachmentBaselineByKey) {
        const parentKey = key;
        const baseline = this.attachmentBaselineByKey[key];
        if (Object.keys(baseline).length === 0) {
          continue;
        }

        // Use the same method as other selection operations for consistency
        // This ensures we match by userData.parent first, which is the most specific
        const obj = this.findObjectByParentKey(parentKey);
        if (!obj) {
          console.warn(`Object not found for ${parentKey}`);
          continue;
        }

        console.log(
          `Resetting object ${obj.name || "unnamed"} for key ${parentKey}`
        );

        // Apply transform to object from baseline
        obj.position.set(
          (baseline as any).tx ?? obj.position.x,
          (baseline as any).ty ?? obj.position.y,
          (baseline as any).tz ?? obj.position.z
        );
        obj.rotation.set(
          toRad((baseline as any).rx ?? 0),
          toRad((baseline as any).ry ?? 0),
          toRad((baseline as any).rz ?? 0)
        );
        obj.scale.set(
          (baseline as any).sx ?? obj.scale.x,
          (baseline as any).sy ?? obj.scale.y,
          (baseline as any).sz ?? obj.scale.z
        );

        // Update matrices to ensure transform is properly applied
        obj.updateMatrix();
        obj.updateMatrixWorld(true);

        // Update store attachmentData back to baseline
        const existing = store.getSelectedAsset(parentKey);
        if (existing) {
          existing.assetNode.attachmentData = {
            ...(baseline as Record<string, number>)
          } as Record<string, number>;
          store.setSelectedAsset(parentKey, { ...existing });
        }
      }

      // Ensure controls update
      if (this.transformControls && this.transformControls.object) {
        this.transformControls.updateMatrixWorld();
      }
    } catch (error) {
      console.error("Failed to reset all attachments:", error);
    }
  }

  /**
   * Attach gizmo to a light object
   */
  attachLightToGizmo(light: THREE.Light): void {
    if (this.transformControls) {
      this.transformControls.attach(light);
      this.transformControls.visible = true;
    }
  }

  /**
   * Detach gizmo from current object
   */
  detachGizmo(): void {
    if (this.transformControls) {
      this.transformControls.detach();
    }
  }

  getRenderNeedsUpdate() {
    return this.renderNeedsUpdate;
  }

  setRenderNeedsUpdate(value: boolean) {
    this.renderNeedsUpdate = value;
  }

  getOrbitControlsTarget() {
    return this.orbitControls?.target;
  }

  setRotationEnabled(enabled: boolean) {
    if (!this.orbitControls) return;
    this.orbitControls.enableRotate = enabled;
  }
}
